import { ContentWrapper } from '@/components/_common';
import { Col, Descriptions, Row, Typography } from 'antd';
import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { useWorkorderDetailContext } from '@/components/Movements/WorkOrders/Details/WorkorderDetailProvider';
import { useWindowSize } from '@/hooks';
import { applyCpfCnpjMask, applyMask } from '@/utils/mask';
import { ContactType } from '@/core/domain/_shared/ContactType';
import flatContacts from '@/utils/functions/flatContacts';
import { cepMask, phoneMask } from '@/utils/mask/commonMasks';
import { useCustomerContacts } from '@/core/domain/people/CustomerContacts/CustomerContacts.hooks';
import { useAccessoryListByVehicle } from '@/core/domain/people/Accessory/Accessory.hooks';
import { useCustomerAddresses } from '@/core/domain/people/CustomerAddresses/CustomerAddresses.hooks';

const ReplicateComplementDetailsCard: React.FC = () => {
  const intl = useIntl();
  const { isMobile } = useWindowSize();

  const {
    workorder: { data: workOrder },
  } = useWorkorderDetailContext();

  const customerContacts = useCustomerContacts(workOrder?.customer?.id);
  const customerAddress = useCustomerAddresses(workOrder?.customer?.id);

  const phoneNumberMain = flatContacts(customerContacts.data).find(
    (contact) =>
      contact.contactType === ContactType.Phone ||
      contact.contactType === ContactType.Mobile,
  )?.value;

  const { data: vehicleAccessories } = useAccessoryListByVehicle(
    workOrder?.vehicle?.id,
  );

  const accessoriesDescription = useMemo(() => {
    if (!vehicleAccessories?.length) return '-';

    return vehicleAccessories
      .map((acessory) => acessory.description)
      .join(', ');
  }, [vehicleAccessories]);

  return (
    <ContentWrapper style={{ paddingBottom: '30px' }}>
      <Row gutter={[24, 24]} style={{ marginBottom: 24 }} align="middle">
        <Col flex={1}>
          <Typography.Title level={4} style={{ margin: 0 }}>
            {intl.formatMessage({ id: 'workOrders.details.clientsData' })}
          </Typography.Title>
        </Col>
      </Row>

      <Descriptions
        column={{ xs: 1, sm: 1, md: 2, xl: 3 }}
        layout={isMobile ? 'vertical' : 'horizontal'}
        className="workorder-descriptions"
        style={{ marginBottom: 24 }}
      >
        <Descriptions.Item
          label={intl.formatMessage({
            id: 'workOrders.domain.customer',
          })}
        >
          {workOrder?.customer?.name || '-'}
        </Descriptions.Item>

        <Descriptions.Item
          label={intl.formatMessage({
            id: 'customers.details.generalInfo.form.cpfCnpj',
          })}
        >
          {workOrder?.customer?.cpfCnpj
            ? applyCpfCnpjMask(workOrder?.customer?.cpfCnpj)
            : '-'}
        </Descriptions.Item>

        <Descriptions.Item
          label={intl.formatMessage({
            id: 'commons.contact.contactType.email',
          })}
        >
          {flatContacts(customerContacts.data).find(
            (contact) => contact.contactType === ContactType.Email,
          )?.value || '-'}
        </Descriptions.Item>

        <Descriptions.Item
          label={intl.formatMessage({
            id: 'commons.contact.contactType.phone',
          })}
        >
          {phoneNumberMain
            ? phoneNumberMain.length <= 11
              ? applyMask(phoneNumberMain, phoneMask)
              : phoneNumberMain
            : '-'}
        </Descriptions.Item>

        <Descriptions.Item
          label={intl.formatMessage({
            id: 'commons.address',
          })}
        >
          {!!customerAddress.data?.data?.length
            ? `${customerAddress.data?.data[0]?.street}, ${
                customerAddress.data?.data[0]?.number
              }, ${customerAddress.data?.data[0]?.district}, ${applyMask(
                customerAddress.data?.data[0]?.zipCode,
                cepMask,
              )}`
            : '-'}
        </Descriptions.Item>

        <Descriptions.Item
          label={`${intl.formatMessage({
            id: 'commons.others',
          })} ${intl.formatMessage({ id: 'commons.contacts' })}`}
        >
          {(() => {
            const contacts = flatContacts(customerContacts.data).filter(
              (contact) => contact.contactType !== ContactType.Email,
            );

            if (!contacts?.length) return '-';

            return contacts.map(({ value: phone }) =>
              phone
                ? phone.length <= 11
                  ? `${applyMask(phone, phoneMask)}, `
                  : `${phone}, `
                : '-, ',
            );
          })()}
        </Descriptions.Item>
      </Descriptions>

      <Descriptions
        column={{ xs: 1, sm: 2, md: 3, xl: 4 }}
        title={intl.formatMessage({
          id: 'workOrders.details.vehicleData',
        })}
        layout={isMobile ? 'vertical' : 'horizontal'}
        className="workorder-descriptions"
      >
        <Descriptions.Item
          label={intl.formatMessage({
            id: 'workOrders.domain.vehicle',
          })}
        >
          {!!workOrder?.vehicle
            ? `${workOrder?.vehicle?.brand} - ${workOrder?.vehicle?.model}`
            : '-'}
        </Descriptions.Item>

        <Descriptions.Item
          label={intl.formatMessage({
            id: 'workOrders.domain.vehicle.licensePlate',
          })}
        >
          {workOrder?.vehicle?.licensePlate || '-'}
        </Descriptions.Item>

        <Descriptions.Item
          label={intl.formatMessage({
            id: 'customers.domain.vehicle.chassi',
          })}
        >
          {workOrder?.vehicle?.chassi || '-'}
        </Descriptions.Item>

        <Descriptions.Item
          label={intl.formatMessage({
            id: 'customers.domain.vehicle.accessories',
          })}
        >
          {accessoriesDescription}
        </Descriptions.Item>

        <Descriptions.Item label="Cor">
          <button
            style={{
              width: 20,
              height: 20,
              borderRadius: 4,
              backgroundColor: workOrder?.vehicle?.color?.split('.')[1],
              border: 'none',
              cursor: 'default',
            }}
          />
        </Descriptions.Item>

        <Descriptions.Item label="Combustível">
          {workOrder?.vehicle?.fuelName}
        </Descriptions.Item>

        <Descriptions.Item label="Ano de fabricação/modelo">
          {workOrder?.vehicle
            ? `${workOrder?.vehicle?.yearFabrication}/${workOrder?.vehicle?.yearModel}`
            : '-'}
        </Descriptions.Item>
      </Descriptions>
    </ContentWrapper>
  );
};

export default ReplicateComplementDetailsCard;
